import { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Chip, Divider,
} from '@mui/material';
import { Pets as PetsIcon } from '@mui/icons-material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';

interface Pet {
  id: number; name: string; species: string; breed: string;
  date_of_birth: string; sex: string; color: string;
  microchip_number: string; is_neutered: number; weight_kg: number;
}

interface Vaccination {
  id: number; pet_name: string; vaccine_name: string;
  administered_date: string; next_due_date: string;
}

const PortalPetsPage = () => {
  const [pets, setPets] = useState<Pet[]>([]);
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      portalApi.get<{ data: Pet[] }>(API_ENDPOINTS.PORTAL.PETS),
      portalApi.get<{ data: Vaccination[] }>(API_ENDPOINTS.PORTAL.VACCINATIONS),
    ]).then(([petsRes, vacRes]) => {
      setPets(Array.isArray(petsRes.data) ? petsRes.data : []);
      setVaccinations(Array.isArray(vacRes.data) ? vacRes.data : []);
    }).catch(() => setError('Failed to load pets'))
      .finally(() => setLoading(false));
  }, []);

  const dueSoonColor = (dateStr: string): 'error' | 'warning' | 'success' => {
    const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
    if (days <= 0) return 'error';
    if (days <= 30) return 'warning';
    return 'success';
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Pets</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {pets.map(p => {
          const petVaccinations = vaccinations.filter(v => v.pet_name === p.name);
          return (
            <Grid item xs={12} md={6} key={p.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PetsIcon color="primary" />
                    <Typography variant="h6">{p.name}</Typography>
                    {p.is_neutered ? <Chip label="Neutered" size="small" color="info" /> : null}
                  </Box>
                  <Grid container spacing={1}>
                    {p.species && <Grid item xs={6}><Typography variant="body2"><strong>Species:</strong> {p.species}</Typography></Grid>}
                    {p.breed && <Grid item xs={6}><Typography variant="body2"><strong>Breed:</strong> {p.breed}</Typography></Grid>}
                    {p.sex && <Grid item xs={6}><Typography variant="body2"><strong>Sex:</strong> {p.sex}</Typography></Grid>}
                    {p.color && <Grid item xs={6}><Typography variant="body2"><strong>Color:</strong> {p.color}</Typography></Grid>}
                    {p.date_of_birth && <Grid item xs={6}><Typography variant="body2"><strong>DOB:</strong> {new Date(p.date_of_birth).toLocaleDateString()}</Typography></Grid>}
                    {p.weight_kg && <Grid item xs={6}><Typography variant="body2"><strong>Weight:</strong> {p.weight_kg} kg</Typography></Grid>}
                    {p.microchip_number && <Grid item xs={12}><Typography variant="body2"><strong>Microchip:</strong> {p.microchip_number}</Typography></Grid>}
                  </Grid>

                  {petVaccinations.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Vaccinations</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {petVaccinations.map(v => (
                          <Chip
                            key={v.id}
                            label={`${v.vaccine_name}${v.next_due_date ? ` — due ${new Date(v.next_due_date).toLocaleDateString()}` : ''}`}
                            size="small"
                            color={v.next_due_date ? dueSoonColor(v.next_due_date) : 'default'}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {pets.length === 0 && (
          <Grid item xs={12}>
            <Typography color="textSecondary" textAlign="center">No pets found on your account.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PortalPetsPage;
